import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../components/config.js';
import { CIP_APP_URL, CIP_RSMIN_TENANT_SLUG } from '../components/config.js';
import './ConsultationPage.css';

const ConsultationPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [connectorConfig, setConnectorConfig] = useState(null);
  const [cipConsultationView, setCipConsultationView] = useState('consultation');
  const authToken = localStorage.getItem('authToken');

  // Form data state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    mapData: '',
    status: 'draft',
    isPubliclyVisible: true,
    priority: 'medium',
    expectedCompletionDate: '',
    tags: '',
    image: null
  });

  // Fetch all projects
  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/projects`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setProjects(response.data.projects);
      setError('');
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchConnectorConfig = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/connector/config`, {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        });
        setConnectorConfig(response.data?.config || null);
      } catch {
        setConnectorConfig(null);
      }
    };

    if (authToken) {
      fetchConnectorConfig();
    }
  }, [authToken]);

  // Handle location data from location picker
  useEffect(() => {
    if (location.state?.locationData) {
      console.log('Received location data:', location.state.locationData);
      setFormData(prev => ({
        ...prev,
        mapData: JSON.stringify(location.state.locationData.geometry, null, 2)
      }));
      
      // Make sure the form is visible
      if (!showCreateForm) {
        setShowCreateForm(true);
      }
      
      // If we have a projectId, restore the editing state
      if (location.state?.projectId) {
        const project = projects.find(p => p._id === location.state.projectId);
        if (project && !editingProject) {
          setEditingProject(project);
          // Restore form data but keep the new mapData
          const newMapData = formData.mapData;
          setFormData({
            name: project.name,
            description: project.description,
            mapData: newMapData, // Keep the newly selected location
            status: project.status,
            isPubliclyVisible: project.isPubliclyVisible,
            priority: project.priority,
            expectedCompletionDate: project.expectedCompletionDate ? 
              new Date(project.expectedCompletionDate).toISOString().split('T')[0] : '',
            tags: project.tags ? project.tags.join(', ') : '',
            image: null
          });
        }
      }
      
      // Clear the location state to prevent re-setting
      window.history.replaceState({}, document.title);
    } else if (location.state?.cancelledFromPicker && location.state?.projectId) {
      // User cancelled from picker while editing - restore editing state
      const project = projects.find(p => p._id === location.state.projectId);
      if (project) {
        setEditingProject(project);
        setShowCreateForm(true);
        // Restore full form data
        setFormData({
          name: project.name,
          description: project.description,
          mapData: project.mapData || '',
          status: project.status,
          isPubliclyVisible: project.isPubliclyVisible,
          priority: project.priority,
          expectedCompletionDate: project.expectedCompletionDate ? 
            new Date(project.expectedCompletionDate).toISOString().split('T')[0] : '',
          tags: project.tags ? project.tags.join(', ') : '',
          image: null
        });
      }
      
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state, showCreateForm, projects, editingProject, formData.mapData]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      mapData: '',
      status: 'draft',
      isPubliclyVisible: true,
      priority: 'medium',
      expectedCompletionDate: '',
      tags: '',
      image: null
    });
    setShowCreateForm(false);
    setEditingProject(null);
  };

  const openLocationPicker = () => {
    let existingLocation = null;
    
    // Safely parse existing mapData
    if (formData.mapData) {
      try {
        existingLocation = JSON.parse(formData.mapData);
      } catch (error) {
        console.error('Error parsing existing mapData:', error);
        existingLocation = null;
      }
    }
    
    navigate('/staff/consultation/location-picker', {
      state: {
        returnPath: '/staff/consultation',
        existingLocation: existingLocation,
        projectId: editingProject?._id
      }
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'image' && formData[key]) {
          submitData.append('image', formData[key]);
        } else if (key !== 'image') {
          submitData.append(key, formData[key]);
        }
      });

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      };

      if (editingProject) {
        await axios.put(`${API_URL}/api/projects/${editingProject._id}`, submitData, config);
      } else {
        await axios.post(`${API_URL}/api/projects`, submitData, config);
      }

      resetForm();
      fetchProjects();
    } catch (err) {
      console.error('Error saving project:', err);
      setError('Failed to save project');
    }
  };

  const handleEdit = (project) => {
    setFormData({
      name: project.name,
      description: project.description,
      mapData: project.mapData || '',
      status: project.status,
      isPubliclyVisible: project.isPubliclyVisible,
      priority: project.priority,
      expectedCompletionDate: project.expectedCompletionDate ? 
        new Date(project.expectedCompletionDate).toISOString().split('T')[0] : '',
      tags: project.tags ? project.tags.join(', ') : '',
      image: null
    });
    setEditingProject(project);
    setShowCreateForm(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      fetchProjects();
    } catch (err) {
      console.error('Error deleting project:', err);
      setError('Failed to delete project');
    }
  };

  const handleViewComments = async (projectId) => {
    try {
      const response = await axios.get(`${API_URL}/api/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      setSelectedProject(response.data);
    } catch (err) {
      console.error('Error fetching project details:', err);
      setError('Failed to fetch project details');
    }
  };

  const handleDeleteComment = async (projectId, commentId) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/projects/${projectId}/comments/${commentId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      // Refresh the selected project to show updated comments
      handleViewComments(projectId);
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      draft: 'status-draft',
      active: 'status-active',
      completed: 'status-completed',
      cancelled: 'status-cancelled'
    };
    
    return <span className={`status-badge ${statusClasses[status]}`}>{status}</span>;
  };

  const getPriorityBadge = (priority) => {
    const priorityClasses = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent'
    };
    
    return <span className={`priority-badge ${priorityClasses[priority]}`}>{priority}</span>;
  };

  if (loading) return <div className="consultation-page"><p>Loading projects...</p></div>;

  const embedCfg = connectorConfig?.embedSettings || {};
  const cipIntegrationEnabled = Boolean(embedCfg.cipIntegrationEnabled);
  const integrationMode = embedCfg.integrationMode === 'live' ? 'live' : 'testing';
  const cipBase = String(embedCfg.cipBaseUrl || CIP_APP_URL || '').replace(/\/$/, '');
  const canShowCipEmbed = cipIntegrationEnabled && Boolean(cipBase) && Boolean(authToken);
  const consultationNext = `/t/${CIP_RSMIN_TENANT_SLUG}/consultation?embedded=1&embedHost=rs&linkMode=${integrationMode}`;
  const communityDataNext = `/t/${CIP_RSMIN_TENANT_SLUG}/community-data?embedded=1&embedHost=rs&linkMode=${integrationMode}`;
  const cipConsultationUrl = canShowCipEmbed
    ? `${cipBase}/auth/rsmin-callback?token=${encodeURIComponent(authToken)}&next=${encodeURIComponent(consultationNext)}`
    : '';
  const cipCommunityDataUrl = canShowCipEmbed
    ? `${cipBase}/auth/rsmin-callback?token=${encodeURIComponent(authToken)}&next=${encodeURIComponent(communityDataNext)}`
    : '';
  const activeConsultationUrl = cipConsultationView === 'communityData' ? cipCommunityDataUrl : cipConsultationUrl;

  return (
    <div className="consultation-page">
      {cipIntegrationEnabled && (
        <div style={{
          background: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: 10,
          padding: '14px 16px',
          marginBottom: 18,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <strong style={{ color: '#0c4a6e' }}>CIP Consultation Integration</strong>
              <div style={{ color: '#0369a1', fontSize: 13, marginTop: 3 }}>
                Embedded mode is {integrationMode}. Use CIP consultation tools directly inside RS.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" className="btn-secondary" onClick={() => setCipConsultationView('consultation')}>
                CIP Consultation
              </button>
              <button type="button" className="btn-secondary" onClick={() => setCipConsultationView('communityData')}>
                CIP Community Data
              </button>
            </div>
          </div>

          {canShowCipEmbed ? (
            <div style={{ marginTop: 12, border: '1px solid #7dd3fc', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
              <iframe
                title="CIP Consultation Embedded"
                src={activeConsultationUrl}
                style={{ width: '100%', minHeight: '72vh', border: 0, display: 'block' }}
                loading="lazy"
                referrerPolicy="strict-origin-when-cross-origin"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
              />
            </div>
          ) : (
            <div style={{ marginTop: 10, color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 12px', fontSize: 13 }}>
              CIP integration is enabled, but CIP base URL or login token is missing. Please check Integrations settings and login state.
            </div>
          )}
        </div>
      )}

      <div className="page-header">
        <h2>Project Consultation Management</h2>
        <div className="header-actions">
          <button 
            className="btn-secondary map-view-btn" 
            onClick={() => navigate('/staff/consultation/map')}
          >
            🗺️ Map View
          </button>
          <button 
            className="btn-primary" 
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            {showCreateForm ? 'Cancel' : 'Add New Project'}
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="project-form-container">
          <h3>{editingProject ? 'Edit Project' : 'Create New Project'}</h3>
          <form onSubmit={handleSubmit} className="project-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Project Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="priority">Priority</label>
                <select
                  id="priority"
                  name="priority"
                  value={formData.priority}
                  onChange={handleInputChange}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description *</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows="4"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="expectedCompletionDate">Expected Completion Date</label>
                <input
                  type="date"
                  id="expectedCompletionDate"
                  name="expectedCompletionDate"
                  value={formData.expectedCompletionDate}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="image">Project Image</label>
                <input
                  type="file"
                  id="image"
                  name="image"
                  accept="image/*"
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="tags">Tags (comma-separated)</label>
                <input
                  type="text"
                  id="tags"
                  name="tags"
                  value={formData.tags}
                  onChange={handleInputChange}
                  placeholder="tag1, tag2, tag3"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="mapData">Map Data (GeoJSON or coordinates)</label>
              <div className="map-data-container">
                <textarea
                  id="mapData"
                  name="mapData"
                  value={formData.mapData}
                  onChange={handleInputChange}
                  rows="6"
                  placeholder="Enter GeoJSON data or coordinates manually, or use the location picker"
                />
                <button 
                  type="button"
                  className="btn-secondary location-picker-btn"
                  onClick={openLocationPicker}
                >
                  📍 Pick
                </button>
              </div>
              {formData.mapData && (
                <small className="map-data-preview">
                  Location data set ({formData.mapData.length} characters)
                </small>
              )}
            </div>

            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="isPubliclyVisible"
                name="isPubliclyVisible"
                checked={formData.isPubliclyVisible}
                onChange={handleInputChange}
              />
              <label htmlFor="isPubliclyVisible">Allow public comments</label>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingProject ? 'Update Project' : 'Create Project'}
              </button>
              <button type="button" className="btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Projects List */}
      <div className="projects-list">
        <h3>All Projects ({projects.length})</h3>
        
        {projects.length === 0 ? (
          <div className="no-projects">
            <p>No projects found. Create your first project!</p>
          </div>
        ) : (
          <div className="projects-grid">
            {projects.map(project => (
              <div key={project._id} className="project-card">
                <div className="project-header">
                  <h4>{project.name}</h4>
                  <div className="project-badges">
                    {getStatusBadge(project.status)}
                    {getPriorityBadge(project.priority)}
                  </div>
                </div>
                
                {project.imageUrl && (
                  <div className="project-image">
                    <img src={`${API_URL}${project.imageUrl}`} alt={project.name} />
                  </div>
                )}
                
                <div className="project-content">
                  <p className="project-description">
                    {project.description.length > 150 
                      ? `${project.description.substring(0, 150)}...`
                      : project.description
                    }
                  </p>
                  
                  <div className="project-meta">
                    <span>Created: {formatDate(project.createdAt)}</span>
                    <span>Comments: {project.commentCount}</span>
                    {project.isPubliclyVisible && (
                      <span className="public-indicator">🌐 Public</span>
                    )}
                  </div>
                  
                  {project.tags && project.tags.length > 0 && (
                    <div className="project-tags">
                      {project.tags.map((tag, index) => (
                        <span key={index} className="tag">{tag}</span>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="project-actions">
                  <button 
                    className="btn-small btn-view" 
                    onClick={() => handleViewComments(project._id)}
                  >
                    View Details ({project.commentCount} comments)
                  </button>
                  <button 
                    className="btn-small btn-edit" 
                    onClick={() => handleEdit(project)}
                  >
                    Edit
                  </button>
                  <button 
                    className="btn-small btn-delete" 
                    onClick={() => handleDelete(project._id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Project Details Modal */}
      {selectedProject && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSelectedProject(null); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedProject.name}</h3>
              <button className="modal-close" onClick={() => setSelectedProject(null)}>
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="project-details">
                <div className="detail-section">
                  <h4>Project Information</h4>
                  <p><strong>Description:</strong> {selectedProject.description}</p>
                  <p><strong>Status:</strong> {getStatusBadge(selectedProject.status)}</p>
                  <p><strong>Priority:</strong> {getPriorityBadge(selectedProject.priority)}</p>
                  <p><strong>Created:</strong> {formatDate(selectedProject.createdAt)}</p>
                  {selectedProject.expectedCompletionDate && (
                    <p><strong>Expected Completion:</strong> {formatDate(selectedProject.expectedCompletionDate)}</p>
                  )}
                </div>
                
                {selectedProject.mapData && (
                  <div className="detail-section">
                    <h4>Location Data</h4>
                    <div className="location-display">
                      <pre className="location-data">{JSON.stringify(JSON.parse(selectedProject.mapData), null, 2)}</pre>
                      <button 
                        className="btn-secondary edit-location-btn"
                        onClick={() => {
                          setEditingProject(selectedProject);
                          setFormData({
                            name: selectedProject.name,
                            description: selectedProject.description,
                            mapData: selectedProject.mapData || '',
                            status: selectedProject.status,
                            isPubliclyVisible: selectedProject.isPubliclyVisible,
                            priority: selectedProject.priority,
                            expectedCompletionDate: selectedProject.expectedCompletionDate || '',
                            tags: selectedProject.tags || '',
                            image: null
                          });
                          setSelectedProject(null);
                          openLocationPicker();
                        }}
                      >
                        📍 Edit Location
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="detail-section">
                  <h4>Anonymous Comments ({selectedProject.comments.length})</h4>
                  {selectedProject.comments.length === 0 ? (
                    <p className="no-comments">No comments yet.</p>
                  ) : (
                    <div className="comments-list">
                      {selectedProject.comments.map(comment => (
                        <div key={comment._id} className="comment">
                          <div className="comment-content">
                            <p>{comment.content}</p>
                            <div className="comment-meta">
                              <span>Submitted: {formatDate(comment.submittedAt)}</span>
                              {comment.ipAddress && (
                                <span className="comment-ip">IP: {comment.ipAddress}</span>
                              )}
                              <button 
                                className="btn-small btn-delete"
                                onClick={() => handleDeleteComment(selectedProject._id, comment._id)}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationPage;